import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import {
  fetchAllTemplateCategories,
  createTemplateCategory,
  updateTemplateCategory,
  deleteTemplateCategory,
} from '../../redux/slices/templateCategorySlice';

// Hazır renk paleti — 12 seçenek
const COLOR_PALETTE = [
  { label: 'Mavi',       value: '#1976D2' },
  { label: 'Yeşil',      value: '#388E3C' },
  { label: 'Turuncu',    value: '#F57C00' },
  { label: 'Kırmızı',   value: '#D32F2F' },
  { label: 'Mor',        value: '#7B1FA2' },
  { label: 'Koyu Mavi', value: '#0288D1' },
  { label: 'Pembe',      value: '#C2185B' },
  { label: 'Sarı',       value: '#F9A825' },
  { label: 'Kahve',      value: '#5D4037' },
  { label: 'Camgöbeği', value: '#00796B' },
  { label: 'Lacivert',   value: '#303F9F' },
  { label: 'Gri',        value: '#607D8B' },
];

const EMPTY_FORM = { name: '', color: '#607D8B', order: 0, isActive: true };

const CategoryManagementPage = () => {
  const dispatch = useDispatch();
  const { adminList, adminLoading, error } = useSelector(
    (state) => state.templateCategories
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    dispatch(fetchAllTemplateCategories());
  }, [dispatch]);

  // ---- Dialog İşlemleri ----
  const handleAdd = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, order: adminList.length + 1 });
    setFormError('');
    setDialogOpen(true);
  };

  const handleEdit = (cat) => {
    setEditingId(cat._id);
    setForm({
      name: cat.name,
      color: cat.color || '#607D8B',
      order: cat.order || 0,
      isActive: cat.isActive,
    });
    setFormError('');
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setFormError('');
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setFormError('Kategori adı gereklidir');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await dispatch(updateTemplateCategory({ id: editingId, ...form })).unwrap();
        showSnackbar('Kategori başarıyla güncellendi');
      } else {
        await dispatch(createTemplateCategory(form)).unwrap();
        showSnackbar('Kategori başarıyla oluşturuldu');
      }
      setDialogOpen(false);
      dispatch(fetchAllTemplateCategories());
    } catch (err) {
      setFormError(err?.message || 'İşlem sırasında bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`"${cat.name}" kategorisini pasife almak istiyor musunuz?`)) return;
    try {
      await dispatch(deleteTemplateCategory(cat._id)).unwrap();
      showSnackbar('Kategori pasife alındı');
      dispatch(fetchAllTemplateCategories());
    } catch (err) {
      showSnackbar(err?.message || 'Silme işlemi sırasında hata oluştu', 'error');
    }
  };

  const handleReactivate = async (cat) => {
    try {
      await dispatch(updateTemplateCategory({ id: cat._id, isActive: true })).unwrap();
      showSnackbar('Kategori aktif edildi');
      dispatch(fetchAllTemplateCategories());
    } catch (err) {
      showSnackbar(err?.message || 'İşlem sırasında hata oluştu', 'error');
    }
  };

  const handleOrderChange = async (cat, direction) => {
    const newOrder = direction === 'up' ? cat.order - 1 : cat.order + 1;
    if (newOrder < 0) return;
    try {
      await dispatch(updateTemplateCategory({ id: cat._id, order: newOrder })).unwrap();
      dispatch(fetchAllTemplateCategories());
    } catch (err) {
      showSnackbar('Sıra güncellenirken hata oluştu', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
    setTimeout(() => setSnackbar((s) => ({ ...s, open: false })), 3000);
  };

  return (
    <Container maxWidth="lg">
      {/* Başlık */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CategoryIcon color="primary" />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Şablon Kategori Yönetimi
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Şablon kategorilerini ekleyin, düzenleyin veya pasife alın
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          Yeni Kategori
        </Button>
      </Box>

      {/* Snackbar */}
      {snackbar.open && (
        <Alert severity={snackbar.severity} sx={{ mb: 2 }}>
          {snackbar.message}
        </Alert>
      )}

      {/* Global hata */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tablo */}
      {adminLoading ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Kategoriler yükleniyor...
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sıra</TableCell>
                  <TableCell>Renk</TableCell>
                  <TableCell>Kategori Adı</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {adminList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">Henüz kategori eklenmemiş</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  adminList.map((cat) => (
                    <TableRow
                      key={cat._id}
                      sx={{ opacity: cat.isActive ? 1 : 0.5 }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2">{cat.order}</Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Tooltip title="Yukarı taşı">
                              <span>
                                <IconButton size="small" onClick={() => handleOrderChange(cat, 'up')} disabled={!cat.isActive}>
                                  <ArrowUpIcon fontSize="inherit" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Aşağı taşı">
                              <span>
                                <IconButton size="small" onClick={() => handleOrderChange(cat, 'down')} disabled={!cat.isActive}>
                                  <ArrowDownIcon fontSize="inherit" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            backgroundColor: cat.color || '#607D8B',
                            border: '2px solid',
                            borderColor: 'divider',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="medium">{cat.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={cat.isActive ? 'Aktif' : 'Pasif'}
                          color={cat.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Tooltip title="Düzenle">
                            <IconButton size="small" color="primary" onClick={() => handleEdit(cat)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {cat.isActive ? (
                            <Tooltip title="Pasife Al">
                              <IconButton size="small" color="error" onClick={() => handleDelete(cat)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Aktif Et">
                              <Button size="small" variant="outlined" onClick={() => handleReactivate(cat)}>
                                Aktif Et
                              </Button>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Ekle/Düzenle Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? 'Kategori Düzenle' : 'Yeni Kategori'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}

            <TextField
              label="Kategori Adı"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              required
              autoFocus
              placeholder="Örn: Gayrimenkul"
            />

            <TextField
              label="Sıra No"
              type="number"
              value={form.order}
              onChange={(e) => setForm((f) => ({ ...f, order: parseInt(e.target.value) || 0 }))}
              fullWidth
              inputProps={{ min: 0 }}
              helperText="Düşük numara öne gelir"
            />

            {/* Renk Seçimi */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Renk Seçimi
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {COLOR_PALETTE.map((c) => (
                  <Tooltip key={c.value} title={c.label}>
                    <Box
                      onClick={() => setForm((f) => ({ ...f, color: c.value }))}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: c.value,
                        cursor: 'pointer',
                        border: form.color === c.value ? '3px solid #000' : '2px solid transparent',
                        outline: form.color === c.value ? '2px solid #fff' : 'none',
                        transition: 'transform 0.1s',
                        '&:hover': { transform: 'scale(1.2)' },
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: form.color,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Seçili: {form.color}
                </Typography>
              </Box>
            </Box>

            {editingId && (
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                  />
                }
                label="Aktif"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CategoryManagementPage;
